import React, { useState } from "react";
import TurnTimer from "./TurnTimer";
import { useTimer } from "../contexts/TimerContext";
import { authenticatedFetch } from "../utils/api";
import ExtendTimeModal from "./ExtendTimeModal";

interface DashboardHeaderProps {
  title: string;
  onLogout: () => void;
  onRefresh: () => void;
  timerSeconds?: number | null;
  showPauseButton?: boolean;
  gameId?: string | null; // Optional game ID for WebSocket broadcast
  timerEnabled?: boolean; // Whether timer should be displayed
  gameStatus?: string | null; // Game status to display
  currentTurn?: number | null; // Current turn number
  maxTurns?: number | null; // Maximum number of turns
  isLoadingTurn?: boolean; // Whether turn information is loading
}

export default function DashboardHeader({ title, onLogout, onRefresh, timerSeconds: timerSecondsProp, showPauseButton = false, gameId, timerEnabled = true, gameStatus, currentTurn, maxTurns, isLoadingTurn = false }: DashboardHeaderProps) {
  const { timerSeconds, isPaused } = useTimer();
  // Use timerSeconds from context (which updates from localStorage polling) instead of prop
  const displayTimerSeconds = timerSeconds ?? timerSecondsProp ?? 0;
  const [showExtendTimeModal, setShowExtendTimeModal] = useState<boolean>(false);

  const handleExtendTime = async (additionalSeconds: number) => {
    // Calculate current_remaining the same way extendTimer does (from localStorage)
    // This ensures consistency, especially when timer has ended
    let currentRemaining = displayTimerSeconds;
    const savedTimer = localStorage.getItem("turnTimer");
    const savedPause = localStorage.getItem("turnTimerPaused");
    
    if (savedTimer) {
      try {
        const timerData = JSON.parse(savedTimer);
        if (timerData.server_end_time && typeof timerData.time_offset === "number") {
          const current_server_time = Date.now() - timerData.time_offset;
          currentRemaining = Math.max(0, Math.floor((timerData.server_end_time - current_server_time) / 1000));
        } else if (timerData.endTime) {
          currentRemaining = Math.max(0, Math.floor((timerData.endTime - Date.now()) / 1000));
        }
      } catch (e) {
        // Fallback to displayTimerSeconds if parsing fails
      }
    } else if (savedPause) {
      try {
        const pauseData = JSON.parse(savedPause);
        if (typeof pauseData.remaining === "number") {
          currentRemaining = pauseData.remaining;
        }
      } catch (e) {
        // Fallback to displayTimerSeconds if parsing fails
      }
    }
    
    // Don't call extendTimer locally - let the WebSocket event handle it for consistency
    // This ensures all clients (including admin) get the same synchronized update
    if (gameId) {
      try {
        // Send current remaining time so backend can calculate and send new total
        // Also send is_paused so backend can include it in the response
        await authenticatedFetch(`/games/timer/extend?game_id=${gameId}&additional_seconds=${additionalSeconds}&current_remaining=${currentRemaining}&is_paused=${isPaused}`, {
          method: "POST",
        });
      } catch (err) {
        console.error("Failed to broadcast timer extend:", err);
        // Continue even if broadcast fails
      }
    }
  };

  return (
    <div style={{ 
      position: "sticky",
      top: 0,
      zIndex: 1000,
      backgroundColor: "white",
      padding: "1rem 2rem",
      marginBottom: "0.5rem",
      borderBottom: "2px solid #dee2e6",
      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
      display: "flex", 
      justifyContent: "space-between", 
      alignItems: "center",
    }}>
      <h1 style={{ margin: 0, color: "#212529" }}>{title}</h1>
      <div style={{ display: "flex", gap: "1rem", alignItems: "center" }}>
        {timerEnabled && !showPauseButton && <TurnTimer initialSeconds={displayTimerSeconds} isPaused={isPaused} />}
        {!isLoadingTurn && currentTurn !== null && maxTurns !== null && !showPauseButton && (
          <span style={{
            padding: "0.5rem 1rem",
            backgroundColor: "#e9ecef",
            color: "#212529",
            borderRadius: "8px",
            fontSize: "0.875rem",
            fontWeight: "bold",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: "80px",
            height: "2.5rem",
            textAlign: "center",
            boxSizing: "border-box",
            margin: 0
          }}>
            Turn: {currentTurn} / {maxTurns}
          </span>
        )}
        {gameStatus && !showPauseButton && (
          <span style={{
            padding: "0.5rem 1rem",
            backgroundColor: gameStatus === "ACTIVE" ? "#d4edda" : 
                           gameStatus === "COMPLETED" ? "#d1ecf1" : "#fff3cd",
            color: gameStatus === "ACTIVE" ? "#155724" : 
                  gameStatus === "COMPLETED" ? "#0c5460" : "#856404",
            borderRadius: "8px",
            fontSize: "0.875rem",
            fontWeight: "bold",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: "80px",
            height: "2.5rem",
            textAlign: "center",
            boxSizing: "border-box",
            margin: 0
          }}>
            Game Status: {gameStatus}
          </span>
        )}
        <button 
          onClick={onLogout} 
          style={{ 
            padding: "0.5rem 1rem", 
            cursor: "pointer",
            backgroundColor: "#dc3545",
            color: "white",
            border: "none",
            borderRadius: "8px",
            fontSize: "0.875rem",
            fontWeight: "bold",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: "80px",
            height: "2.5rem",
            textAlign: "center",
            boxSizing: "border-box",
            margin: 0,
            lineHeight: "1"
          }}
        >
          Logout
        </button>
      </div>
      <ExtendTimeModal
        isOpen={showExtendTimeModal}
        onClose={() => setShowExtendTimeModal(false)}
        onSubmit={handleExtendTime}
      />
    </div>
  );
}
